/**
 * デバイス操作画面
 * デバイス一覧を表示し、選択したデバイスに対して操作（アプリ・リンク・通知・ログ取得）を実行する。
 * モーダル型の操作の追加は device_operations/operationConfig.tsx の DEVICE_OPERATIONS に追加する。
 */
import React, { useRef, useState } from "react";
import { Button, Dropdown, Modal, Space, Tooltip, Typography } from "antd";
import {
  AppstoreOutlined,
  BellOutlined,
  CloudDownloadOutlined,
  DownOutlined,
  InfoCircleOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import axios from "axios";
import { DEVICE_OPERATIONS } from "./device_operations/operationConfig";
import { Device } from "./web_parts/types";
import { useDevices } from "./web_parts/DeviceProvider";
import DeviceListModuleSingle from "./web_parts/DeviceList_module_single";
import WebLinkEditor, { WebLinkEditorHandle } from "./web_parts/WebLinkEditor";
import EmergencyNotificationEditor, { NotificationEditorHandle } from "./web_parts/EmergencyNotificationEditor";
import BbsNotificationEditor from "./web_parts/BbsNotificationEditor";
import MimamoriSettingsEditor from "./web_parts/MimamoriSettingsEditor";
import DeviceLogEditor, { DeviceLogEditorHandle } from "./web_parts/DeviceLogEditor";
import "../../styles/global.css";

const { Text } = Typography;

/**
 * 端末へ device_data_full（実質 linkitems 再取得）を要求する。
 * 「全データ同期」ボタンは UI から外しているが、復帰時はこの関数を呼べばよい。
 *
 * 復帰例:
 *   import { CloudSyncOutlined } from "@ant-design/icons";
 *   <Button icon={<CloudSyncOutlined />} disabled={!selectedDevice}
 *     onClick={() => { ... Modal.confirm → requestDeviceDataFullSync(selectedDevice) }}>
 *     全データ同期
 *   </Button>
 */
export async function requestDeviceDataFullSync(device: Device): Promise<void> {
  if (!device.fcmToken) {
    throw new Error("このデバイスには通知先トークンが設定されていません。");
  }
  const payload = {
    mode: "request",
    command: "device_data_full",
    user: "TANTOU",
    place: "CENTER",
    param1: String(Date.now()),
  };
  await axios.post(
    "/api/sendFCM",
    { tokens: [device.fcmToken], payload },
    { headers: { "Content-Type": "application/json" } }
  );
}

const DeviceOperations: React.FC = () => {
  const { refreshDevices } = useDevices();
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [operationModal, setOperationModal] = useState<{
    id: string;
    device: Device;
  } | null>(null);
  const webLinkRef = useRef<WebLinkEditorHandle>(null);
  const emergencyRef = useRef<NotificationEditorHandle>(null);
  const bbsNotifyRef = useRef<NotificationEditorHandle>(null);
  const mimamoriRef = useRef<{ open: () => void }>(null);
  const deviceLogRef = useRef<DeviceLogEditorHandle>(null);

  const handleOperationClick = (opId: string, device: Device) => {
    setOperationModal({ id: opId, device });
  };

  const closeOperationModal = () => {
    setOperationModal(null);
    refreshDevices();
  };

  const operation = operationModal
    ? DEVICE_OPERATIONS.find((o) => o.id === operationModal.id)
    : null;
  const modalContent =
    operation && operationModal
      ? operation.renderModal(operationModal.device, closeOperationModal)
      : null;

  const selectedLabel = selectedDevice
    ? selectedDevice.deviceName || selectedDevice.serialNumber || selectedDevice.id
    : "未選択";

  const appMenu: MenuProps = {
    items: DEVICE_OPERATIONS.map((op) => ({
      key: op.id,
      icon: op.icon,
      label: op.label,
    })),
    onClick: ({ key }) => {
      if (selectedDevice) handleOperationClick(key, selectedDevice);
    },
  };

  const linkMenu: MenuProps = {
    items: [
      { key: "web", label: "Webリンクを編集" },
      { key: "youtube", label: "Youtubeリンクを編集" },
    ],
    onClick: ({ key }) => {
      webLinkRef.current?.open(key as "web" | "youtube");
    },
  };

  const notifyMenu: MenuProps = {
    items: [
      { key: "emergency", label: "緊急通知設定" },
      { key: "bbs", label: "掲示板通知設定" },
      { key: "mimamori", label: "みまもり設定" },
    ],
    onClick: ({ key }) => {
      if (key === "emergency") emergencyRef.current?.open();
      if (key === "bbs") bbsNotifyRef.current?.open();
      if (key === "mimamori") mimamoriRef.current?.open();
    },
  };

  const logMenu: MenuProps = {
    items: [
      { key: "log_request", label: "ログ取得リクエスト" },
      { key: "log_download", label: "ログのダウンロード" },
    ],
    onClick: ({ key }) => {
      if (key === "log_request") deviceLogRef.current?.openRequest();
      if (key === "log_download") deviceLogRef.current?.openDownload();
    },
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "calc(100vh - 180px)" }}>
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          background: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          padding: "8px 0",
          marginBottom: 8,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Space>
          <Text type="secondary">選択中:</Text>
          <Text strong>{selectedLabel}</Text>
          <Tooltip title="一覧でデバイスを選択してから操作します。登録・削除は「デバイス管理」タブで行います。">
            <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
          </Tooltip>
        </Space>
        <Space wrap>
          <Dropdown menu={appMenu} trigger={["click"]} disabled={!selectedDevice}>
            <Button icon={<AppstoreOutlined />} disabled={!selectedDevice}>
              アプリ <DownOutlined />
            </Button>
          </Dropdown>
          <Dropdown menu={linkMenu} trigger={["click"]} disabled={!selectedDevice}>
            <Button icon={<LinkOutlined />} disabled={!selectedDevice}>
              リンク <DownOutlined />
            </Button>
          </Dropdown>
          <Dropdown menu={notifyMenu} trigger={["click"]} disabled={!selectedDevice}>
            <Button icon={<BellOutlined />} disabled={!selectedDevice}>
              通知 <DownOutlined />
            </Button>
          </Dropdown>
          <Dropdown menu={logMenu} trigger={["click"]} disabled={!selectedDevice}>
            <Button icon={<CloudDownloadOutlined />} disabled={!selectedDevice}>
              ログ取得 <DownOutlined />
            </Button>
          </Dropdown>
        </Space>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <DeviceListModuleSingle
          variant="compact"
          onDeviceSelected={setSelectedDevice}
          selectedDeviceId={selectedDevice?.id}
        />
      </div>

      <WebLinkEditor ref={webLinkRef} selectedDevice={selectedDevice} hideTrigger />
      <EmergencyNotificationEditor ref={emergencyRef} selectedDevice={selectedDevice} hideTrigger />
      <BbsNotificationEditor ref={bbsNotifyRef} selectedDevice={selectedDevice} hideTrigger />
      <MimamoriSettingsEditor ref={mimamoriRef} selectedDevice={selectedDevice} hideTrigger />
      <DeviceLogEditor ref={deviceLogRef} selectedDevice={selectedDevice} />

      {operationModal && modalContent && operation && (
        operation.wrapInModal ? (
          <Modal
            open={!!operationModal}
            onCancel={closeOperationModal}
            footer={null}
            width={Math.min(900, window.innerWidth * 0.95)}
            style={{ top: 20 }}
            destroyOnClose
          >
            {modalContent}
          </Modal>
        ) : (
          modalContent
        )
      )}
    </div>
  );
};

export default DeviceOperations;
